/**
 * Month-grid multi-day event layout.
 *
 * The month view renders one cell per day, stacking that day's events top-to-
 * bottom. Rendered naively, a multi-day event (e.g. a "France trip") is placed
 * independently in each day cell, so its vertical slot depends on whatever else
 * sits in that cell — the continuous bar zig-zags between rows from day to day
 * (it looks "broken"), and on busy days it can be pushed past the visible cap
 * and vanish from the middle of its own span.
 *
 * This module assigns each multi-day event a STABLE lane (row index) for the
 * whole week it appears in, reserving that lane on every day it covers. The bar
 * then renders on the same line across the week (unbroken) and on the top lanes
 * (above single-day events). Lanes are packed per-week with a greedy interval
 * partition, so overlapping trips stack onto distinct lanes and non-overlapping
 * ones reuse the same lane.
 *
 * The grid is assumed to be Sunday-aligned and a whole number of 7-day weeks
 * (as produced by the month view's `getCalendarDays`).
 */
import { parseEventDate, startOfLocalDay, isAllDayish } from '../dateTimeUtils';
import type { EventObject } from '../eventTypes';

// Last calendar day an event actually occupies (date-only, local midnight).
// All-day events use an EXCLUSIVE end date (Google/iCal convention): a one-day
// all-day endAt is stored INCLUSIVE (the last occupied day) — it IS the last
// occupied day, no decrement (#calendar-allday-inclusive). A TIMED event whose
// end falls EXACTLY on local midnight (00:00:00.000) ends at the start of that
// day and occupies no time on it, so it's exclusive (a 9pm–midnight event is a
// single day; a 5-day trip ending midnight shouldn't paint a 6th day). Other
// timed events occupy through their end day as-is. Returns null when no endAt.
// The timed decrement uses setDate(getDate()-1) so it is DST-safe (no 24h math).
export const getLastOccupiedDay = (event: {
  allDay?: boolean;
  startAt: string;
  endAt?: string;
}): Date | null => {
  const end = parseEventDate(event.endAt);
  if (!end) return null;
  const last = startOfLocalDay(end);
  const endsAtLocalMidnight =
    end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0 && end.getMilliseconds() === 0;
  // All-day endAt is stored INCLUSIVE (the last occupied day) — no decrement.
  // Only a TIMED event ending exactly at local midnight is exclusive. Twin of
  // lib/getEventsForDate.ts getLastOccupiedDay — keep in sync.
  // (#calendar-allday-inclusive — was: all-day treated as exclusive -1)
  if (!isAllDayish(event) && endsAtLocalMidnight) {
    last.setDate(last.getDate() - 1);
  }
  return last;
};

// Whether an event spans more than one calendar day (all-day end is exclusive).
export const isMultiDayEvent = (event: EventObject): boolean => {
  if (!event.endAt) return false;
  const start = parseEventDate(event.startAt);
  if (!start) return false;
  const lastOccupiedDay = getLastOccupiedDay(event);
  if (!lastOccupiedDay) return false;
  return lastOccupiedDay.getTime() > startOfLocalDay(start).getTime();
};

interface MultiDaySpan {
  event: EventObject;
  startMs: number; // first occupied day, date-only local ms
  endMs: number; // last occupied day, date-only local ms (inclusive)
}

export interface MonthMultiDayLayout {
  /** dayIndex (flat grid index) -> (lane -> event occupying that lane on that day). */
  byDay: Map<number, Map<number, EventObject>>;
  /** Number of reserved multi-day lanes for each week (index = week number). */
  laneCountByWeek: number[];
}

/**
 * Compute stable per-week lane assignments for the multi-day events in a month
 * grid. `days` is the flat, Sunday-aligned list of day cells; `events` is the
 * full set of visible events (single-day ones are ignored here).
 */
export function layoutMultiDaySpans(
  days: Array<{ date: Date }>,
  events: EventObject[],
): MonthMultiDayLayout {
  const weeks = Math.ceil(days.length / 7);
  const laneCountByWeek = new Array<number>(weeks).fill(0);
  const byDay = new Map<number, Map<number, EventObject>>();
  const gridMs = days.map((d) => startOfLocalDay(d.date).getTime());

  const spans: MultiDaySpan[] = [];
  for (const event of events) {
    if (!isMultiDayEvent(event)) continue;
    const start = parseEventDate(event.startAt);
    const last = getLastOccupiedDay(event);
    if (!start || !last) continue;
    spans.push({ event, startMs: startOfLocalDay(start).getTime(), endMs: last.getTime() });
  }

  for (let week = 0; week < weeks; week++) {
    const colBase = week * 7;
    const weekMs = gridMs.slice(colBase, colBase + 7);

    // Clip each span to the columns it covers within this week.
    const segments: Array<{ event: EventObject; startCol: number; endCol: number }> = [];
    for (const span of spans) {
      let startCol = -1;
      let endCol = -1;
      for (let col = 0; col < weekMs.length; col++) {
        if (weekMs[col] >= span.startMs && weekMs[col] <= span.endMs) {
          if (startCol === -1) startCol = col;
          endCol = col;
        }
      }
      if (startCol === -1) continue;
      segments.push({ event: span.event, startCol, endCol });
    }

    // Greedy interval partition: earliest start first, longer spans first, then
    // a stable id tiebreak. A lane is free once its last occupied column is
    // strictly before this segment's start column.
    segments.sort(
      (a, b) =>
        a.startCol - b.startCol ||
        b.endCol - b.startCol - (a.endCol - a.startCol) ||
        String(a.event.id).localeCompare(String(b.event.id)),
    );

    const laneEnds: number[] = []; // laneEnds[lane] = last column occupied in that lane
    for (const seg of segments) {
      let lane = 0;
      while (lane < laneEnds.length && laneEnds[lane] >= seg.startCol) lane++;
      laneEnds[lane] = seg.endCol;
      for (let col = seg.startCol; col <= seg.endCol; col++) {
        const dayIndex = colBase + col;
        let laneMap = byDay.get(dayIndex);
        if (!laneMap) {
          laneMap = new Map<number, EventObject>();
          byDay.set(dayIndex, laneMap);
        }
        laneMap.set(lane, seg.event);
      }
    }
    laneCountByWeek[week] = laneEnds.length;
  }

  return { byDay, laneCountByWeek };
}
